/**
 * Seller Onboarding API Client
 * Replaces localStorage with database-backed persistence
 */

/**
 * Create or update seller onboarding
 */
export async function createOrUpdateOnboarding(data) {
  const response = await request('/api/seller/onboarding', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.data;
}

/**
 * Get current user's seller onboarding
 */
export async function getSellerOnboarding() {
  const response = await request('/api/seller/onboarding', {
    method: 'GET',
  });
  return response.data;
}

/**
 * Update onboarding status
 */
export async function updateOnboardingStatus(id, statusData) {
  const response = await request(`/api/seller/onboarding/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify(statusData),
  });
  return response.data;
}

/**
 * Update seller onboarding details with geocoding
 * PATCH /api/seller/onboarding/:id
 * 
 * Automatically geocodes address/postcode if changed
 * Persists lat/lng to database for map pinning
 */
export async function updateSellerOnboarding(id, onboardingData) {
  const response = await request(`/api/seller/onboarding/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(onboardingData),
  });
  return response.data;
}

/**
 * Get seller onboarding details by ID
 * GET /api/seller/onboarding/:id
 * 
 * Returns full onboarding record including lat/lng coordinates
 */
export async function getSellerOnboardingById(id) {
  const response = await request(`/api/seller/onboarding/${id}`, {
    method: 'GET',
  });
  return response.data;
}

async function request(path, options = {}) {
  const { getWebEnv } = await import('../env.js');
  const API_URL = getWebEnv().apiUrl;
  const url = `${API_URL.replace(/\/$/, "")}${path}`;

  console.log("🔵 API Request:", { url, method: options.method || 'GET' });

  const token = typeof window !== "undefined" ? localStorage.getItem("break_agency_jwt_token") : null;

  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      },
      ...options
    });

    console.log("🟢 API Response:", { status: res.status, statusText: res.statusText });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || error.detail || res.statusText || 'Request failed');
    }

    return res.json();
  } catch (error) {
    console.error("🔴 API Fetch Error:", error);
    throw error;
  }
}

/**
 * Migrate localStorage data to database
 */
export async function migrateLocalStorageData(localStorageData) {
  const response = await apiClient.post('/seller/onboarding/migrate', {
    localStorageData
  });
  return response.data;
}

/**
 * Compute valuation from Land Registry data
 * POST /api/valuations
 * 
 * Returns real comparable sales and valuation range
 */
export async function computeValuation(onboardingId, postcode, bedrooms, propertyType) {
  const response = await request('/api/valuations', {
    method: 'POST',
    body: JSON.stringify({
      onboardingId,
      postcode,
      bedrooms,
      propertyType
    })
  });
  return response;
}

/**
 * Create valuation request
 */
export async function createValuationRequest(data) {
  const response = await apiClient.post('/seller/onboarding/valuation', data);
  return response.data;
}
